import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { api, type ReviewConfidenceCalibration } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type Counts = ReviewConfidenceCalibration['tiers']['green']
type Timing = NonNullable<ReviewConfidenceCalibration['timing']>

const TIER_LABEL = { green: 'Green (80+)', amber: 'Amber (45-79)', red: 'Red (<45)' } as const

// Same labels the signal chips use, so the table reads the way the row
// panel does; anything unknown falls back to the raw type.
const SIGNAL_LABEL: Record<string, string> = {
  unresolved_issue: 'Unresolved issue',
  explicit_complaint: 'Explicit complaint',
  unanswered_or_slow: 'Unanswered or slow reply',
  coordination_friction: 'Coordination friction',
  went_quiet_after_friction: 'Went quiet after friction',
  no_response_to_review_request: 'No response to review request',
  flat_or_absent_checkout: 'Flat or absent checkout',
  any: 'Any signal at all',
  none: 'No signal',
}

function pct(n: number, d: number) {
  return d ? Math.round((100 * n) / d) : 0
}

const SEGMENTS = [
  { k: 'five_star', cls: 'bg-mint', text: 'five-star' },
  { k: 'sub_five', cls: 'bg-danger', text: 'below five' },
  { k: 'no_review', cls: 'bg-amber', text: 'no review' },
] as const

/**
 * One 100%-stacked outcome bar. Each segment carries a real tooltip
 * (count, share, and what the denominator is) rather than a native title,
 * which only shows after a delay and never on touch.
 */
function OutcomeBar({
  counts,
  label,
  unit = 'resolved',
}: {
  counts: Counts
  label: string
  unit?: 'resolved' | 'stays'
}) {
  const resolved = counts.five_star + counts.sub_five + counts.no_review
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] items-center gap-3 text-xs">
      <span className="truncate font-medium text-text" title={label}>
        {label}
      </span>
      <div
        className="flex h-4 w-full overflow-hidden rounded-full bg-white/10"
        role="img"
        aria-label={`${label}: ${SEGMENTS.map(s => `${s.text} ${pct(counts[s.k], resolved)}%`).join(', ')}`}
      >
        {SEGMENTS.map(s =>
          counts[s.k] > 0 ? (
            <Tooltip key={s.k}>
              <TooltipTrigger asChild>
                <div
                  className={`${s.cls} h-full cursor-default`}
                  style={{
                    width: `${(100 * counts[s.k]) / Math.max(1, resolved)}%`,
                    marginRight: 2,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="font-semibold">
                  {label} · {resolved} {unit}
                </div>
                <div>
                  {s.text}: {counts[s.k]} ({pct(counts[s.k], resolved)}%)
                </div>
                {counts.pending > 0 && (
                  <div className="text-textMuted">
                    {counts.pending} still inside the review window
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          ) : null
        )}
      </div>
    </div>
  )
}

function Widget({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  )
}

/** The study's signal table, from live counts: outcome share per signal type over resolved scored stays. */
function SignalTable({ signals }: { signals: Record<string, Counts> }) {
  const rows = Object.entries(signals)
    .filter(([k, c]) => k !== 'any' && k !== 'none' && c.five_star + c.sub_five + c.no_review > 0)
    .sort((a, b) => {
      const ra = a[1]
      const rb = b[1]
      return (
        pct(rb.sub_five, rb.five_star + rb.sub_five + rb.no_review) -
        pct(ra.sub_five, ra.five_star + ra.sub_five + ra.no_review)
      )
    })
  const footer = (['any', 'none'] as const).filter(k => signals[k])
  if (rows.length === 0 && footer.length === 0) return null

  const cell = (c: Counts, k: (typeof SEGMENTS)[number]['k'], hot: number, hotCls: string) => {
    const resolved = c.five_star + c.sub_five + c.no_review
    const p = pct(c[k], resolved)
    return (
      <td className="px-2 py-1.5 text-right tabular-nums">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-block rounded px-1.5 py-0.5 ${p >= hot ? hotCls : 'text-text'}`}
            >
              {p}%
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {c[k]} of {resolved} resolved stays with this signal
            {c.pending ? ` · ${c.pending} pending` : ''}
          </TooltipContent>
        </Tooltip>
      </td>
    )
  }

  const row = (k: string, c: Counts, bold = false) => (
    <tr key={k} className="border-t border-border">
      <td className={`px-2 py-1.5 ${bold ? 'font-semibold text-text' : 'text-text'}`}>
        {SIGNAL_LABEL[k] ?? k}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-textMuted">{c.n}</td>
      {cell(c, 'five_star', 101, '')}
      {cell(c, 'no_review', 40, 'bg-amber/15 font-semibold text-amber')}
      {cell(c, 'sub_five', 30, 'bg-danger/15 font-semibold text-danger')}
    </tr>
  )

  return (
    <Widget
      title="What each signal meant for the review"
      description="Outcome share per message-thread signal over resolved scored stays. A stay with two signals counts under each."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-textMuted">
              <th className="px-2 py-1 text-left font-medium">Signal</th>
              <th className="px-2 py-1 text-right font-medium">Stays</th>
              <th className="px-2 py-1 text-right font-medium">Five-star</th>
              <th className="px-2 py-1 text-right font-medium">No review</th>
              <th className="px-2 py-1 text-right font-medium">Below five</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, c]) => row(k, c))}
            {footer.map(k => row(k, signals[k], true))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-textMuted">
        Highlighted when no-review reaches 40% or below-five reaches 30%.
      </p>
    </Widget>
  )
}

const CHART_W = 640
const CHART_H = 220
const PAD = { l: 40, r: 16, t: 12, b: 28 }

/**
 * Cumulative share of reviews received by days after checkout, five-star
 * vs below-five, with a hover crosshair. Plain SVG: two lines and a
 * tooltip do not justify a charting library in the bundle.
 */
function TimingChart({ timing }: { timing: Timing }) {
  const [hover, setHover] = useState<number | null>(null)
  const days = timing.five_star_cum.length
  if (days < 2 || timing.reviewed === 0) return null
  const x = (d: number) => PAD.l + (d / (days - 1)) * (CHART_W - PAD.l - PAD.r)
  const y = (p: number) => PAD.t + (1 - p / 100) * (CHART_H - PAD.t - PAD.b)
  const path = (series: number[]) =>
    series.map((p, d) => `${d === 0 ? 'M' : 'L'}${x(d).toFixed(1)},${y(p).toFixed(1)}`).join(' ')
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * CHART_W
    const d = Math.round(((px - PAD.l) / (CHART_W - PAD.l - PAD.r)) * (days - 1))
    setHover(Math.min(days - 1, Math.max(0, d)))
  }
  const tipLeft = hover == null ? 0 : x(hover) > CHART_W * 0.7 ? x(hover) - 168 : x(hover) + 10

  return (
    <Widget title="When a review is going to be five stars, it arrives fast">
      <p className="text-xs text-textMuted">
        Cumulative share of reviews received by days after checkout, all {timing.reviewed} reviewed
        stays.
        {timing.five_share_d0_1 != null && timing.five_share_d8_plus != null && (
          <>
            {' '}
            A review landing on day 0-1 is five-star {timing.five_share_d0_1}% of the time; day 8 or
            later, {timing.five_share_d8_plus}%.
          </>
        )}
      </p>
      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="h-auto w-full min-w-[420px]"
          role="img"
          aria-label={`Five-star reviews reach ${timing.five_star_cum[1]}% by day 1 and ${timing.five_star_cum[7]}% by day 7; below-five reviews ${timing.sub_five_cum[1]}% and ${timing.sub_five_cum[7]}%.`}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {[0, 25, 50, 75, 100].map(p => (
            <g key={p}>
              <line
                x1={PAD.l}
                x2={CHART_W - PAD.r}
                y1={y(p)}
                y2={y(p)}
                stroke="currentColor"
                className="text-white/10"
              />
              <text
                x={PAD.l - 6}
                y={y(p) + 3}
                textAnchor="end"
                className="fill-textMuted text-[10px]"
              >
                {p}%
              </text>
            </g>
          ))}
          {Array.from({ length: days }, (_, d) => d)
            .filter(d => d % 2 === 0)
            .map(d => (
              <text
                key={d}
                x={x(d)}
                y={CHART_H - 8}
                textAnchor="middle"
                className="fill-textMuted text-[10px]"
              >
                d{d}
              </text>
            ))}
          <path d={path(timing.five_star_cum)} fill="none" stroke="#00ba82" strokeWidth={2} />
          <path d={path(timing.sub_five_cum)} fill="none" stroke="#ff4d4f" strokeWidth={2} />
          {hover != null && (
            <g>
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.t}
                y2={CHART_H - PAD.b}
                stroke="currentColor"
                className="text-white/30"
                strokeDasharray="3 3"
              />
              <circle cx={x(hover)} cy={y(timing.five_star_cum[hover])} r={4} fill="#00ba82" />
              <circle cx={x(hover)} cy={y(timing.sub_five_cum[hover])} r={4} fill="#ff4d4f" />
            </g>
          )}
        </svg>
        {hover != null && (
          <div
            className="pointer-events-none absolute top-2 z-10 rounded-md border border-borderStrong bg-surface2 px-3 py-1.5 text-xs text-text shadow-md"
            style={{ left: `${(100 * tipLeft) / CHART_W}%` }}
          >
            <div className="font-semibold">Day {hover} after checkout</div>
            <div>
              <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint" />
              five-star: {timing.five_star_cum[hover]}% of {timing.five_star}
            </div>
            <div>
              <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-danger" />
              below five: {timing.sub_five_cum[hover]}% of {timing.sub_five}
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-4 text-[11px] text-textMuted">
        <span>
          <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint" />
          five-star reviews
        </span>
        <span>
          <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-danger" />
          below-five reviews
        </span>
      </div>
    </Widget>
  )
}

/**
 * How the confidence score is doing against reality: the nightly outcome
 * join (admin-api's review-confidence-calibration.ts) at the bottom of
 * Review Chase. Always open - the operator asked for the evidence to be
 * on the page, not behind a button.
 */
export function CalibrationPanel() {
  const query = useQuery({
    queryKey: ['review-confidence-calibration'],
    queryFn: () => api.getReviewConfidenceCalibration(),
  })
  const cal = query.data?.calibration ?? null

  return (
    <div className="space-y-6">
      <Widget
        title="How the confidence score is doing"
        description="Every score joined to what actually happened, refreshed nightly. No re-reading of messages; the model runs once per stay."
      >
        {query.isLoading && <p className="text-sm text-textMuted">Loading…</p>}
        {query.isError && (
          <p className="text-sm text-danger">Couldn&apos;t load the calibration.</p>
        )}
        {query.isSuccess && !cal && (
          <p className="text-sm text-textMuted">
            No calibration yet - the first nightly run (21:00 UTC) writes it.
          </p>
        )}
        {cal && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                k="Scored"
                v={String(cal.records_scored)}
                tip="Reservations with a completed confidence score."
              />
              <Stat
                k="With an outcome"
                v={String(cal.resolved)}
                tip="Scored stays whose review arrived or whose 14-day window closed with none."
              />
              <Stat
                k="AUC, five vs not"
                v={cal.auc_five_vs_not == null ? '-' : cal.auc_five_vs_not.toFixed(2)}
                sub={cal.auc_five_vs_not == null ? 'needs 5+ of each' : '0.5 = coin flip'}
                tip="Chance a random five-star stay scored higher than a random non-five-star one. 1.0 is perfect ranking."
              />
              <Stat
                k="Reminders"
                v={
                  cal.reminders.clicks === 0
                    ? '-'
                    : `${cal.reminders.converted_within_4_days}/${cal.reminders.clicks}`
                }
                sub={
                  cal.reminders.clicks === 0
                    ? 'no reminder clicks yet'
                    : 'clicks → review in 4 days'
                }
                tip={`${cal.reminders.stays_reminded} stays got a reminder click; ${cal.reminders.stays_skipped} were skipped.`}
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-text">Outcome by tier</div>
              {(['green', 'amber', 'red'] as const).map(t => (
                <OutcomeBar key={t} counts={cal.tiers[t]} label={TIER_LABEL[t]} />
              ))}
              {cal.guest_reviewed_first.n > 0 && (
                <OutcomeBar counts={cal.guest_reviewed_first} label="Guest reviewed first" />
              )}
              {cal.guest_reviewed_first_timing && cal.guest_reviewed_first_timing.n > 0 && (
                <p className="text-[11px] text-textMuted">
                  Guests who reviewed first did so a median{' '}
                  {cal.guest_reviewed_first_timing.median_hours_after_checkout}h after checkout;{' '}
                  {cal.guest_reviewed_first_timing.within_24h} of{' '}
                  {cal.guest_reviewed_first_timing.n} within the first day. No thank-you goes to
                  these stays, so the decision has to be made that early.
                </p>
              )}
              <div className="flex gap-4 text-[11px] text-textMuted">
                <span>
                  <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint" />
                  five-star
                </span>
                <span>
                  <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-danger" />
                  below five
                </span>
                <span>
                  <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-amber" />
                  no review
                </span>
              </div>
            </div>

            <p className="text-[11px] text-textMuted">
              Computed {new Date(cal.computed_at).toLocaleString()}.
            </p>
          </>
        )}
      </Widget>

      {cal && (
        <>
          <div className="grid gap-6 xl:grid-cols-2">
            {cal.signals && <SignalTable signals={cal.signals} />}
            {cal.timing && <TimingChart timing={cal.timing} />}
          </div>

          {cal.misses.length > 0 && (
            <Widget
              title={`Confident scores the review contradicted (${cal.misses.length})`}
              description="Score 75 or above, or no signals at all, and the review came in below five. These are where the prompt gets re-tuned."
            >
              <ul className="space-y-1 text-xs">
                {cal.misses.slice(0, 12).map(m => (
                  <li key={m.reservation_id} className="flex flex-wrap gap-x-3 text-textMuted">
                    <Link
                      to={`/reservations/${m.reservation_id}`}
                      className="font-medium text-mint hover:underline"
                    >
                      {m.reservation_id}
                    </Link>
                    <span>score {m.score}</span>
                    <span className="text-danger">rated {m.rating}</span>
                    <span>
                      {m.signal_count} signal{m.signal_count === 1 ? '' : 's'}
                    </span>
                    {m.checkout_tone && <span>checkout {m.checkout_tone}</span>}
                  </li>
                ))}
              </ul>
            </Widget>
          )}

          {Object.keys(cal.properties).length > 0 && (
            <Widget
              title="Confidence prior by property"
              description="Five-star / below-five / no-review share of every closed stay, the prior the decision line quotes next to the score."
            >
              {Object.entries(cal.properties)
                .sort((a, b) => b[1].stays - a[1].stays)
                .map(([id, p]) => (
                  <OutcomeBar
                    key={id}
                    unit="stays"
                    counts={{
                      n: p.stays,
                      five_star: p.five_star,
                      sub_five: p.sub_five,
                      no_review: p.no_review,
                      pending: 0,
                    }}
                    label={p.name}
                  />
                ))}
            </Widget>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ k, v, sub, tip }: { k: string; v: string; sub?: string; tip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          tabIndex={0}
          className="cursor-default rounded-lg border border-border bg-surface2/50 px-3 py-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
        >
          <div className="text-[11px] text-textMuted">{k}</div>
          <div className="text-lg font-semibold tabular-nums text-text">{v}</div>
          {sub && <div className="text-[11px] text-textMuted">{sub}</div>}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tip}</TooltipContent>
    </Tooltip>
  )
}
